import React from 'react';
import { BarChart, Trash2, Layers, TrendingDown, Calendar } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных измерений
 */
const ComparisonSidebar = ({ 
  savedMeasurements, 
  deleteSavedMeasurement, 
  loadSavedMeasurement 
}) => {
  // Если нет сохраненных измерений
  if (!savedMeasurements || savedMeasurements.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение измерений
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные измерения для отслеживания прогресса.
          Рассчитайте и сохраните ваш процент жира тела, чтобы отслеживать изменения с течением времени.
        </p>
      </div>
    );
  }

  // Сортировка измерений по дате (от новых к старым)
  const sortedMeasurements = [...savedMeasurements].sort((a, b) => 
    new Date(b.date) - new Date(a.date)
  );

  // Расчет прогресса (если есть хотя бы 2 измерения)
  const showProgress = sortedMeasurements.length >= 2;
  let progressData = null;

  if (showProgress) {
    const latest = sortedMeasurements[0];
    const previous = sortedMeasurements[1];
    
    const bfChange = latest.results.bodyFatPercentage - previous.results.bodyFatPercentage;
    const weightChange = latest.results.weight - previous.results.weight;
    const fatMassChange = latest.results.bodyComposition.fatMass - previous.results.bodyComposition.fatMass;
    const leanMassChange = latest.results.bodyComposition.leanMass - previous.results.bodyComposition.leanMass;
    
    progressData = {
      bfChange,
      weightChange,
      fatMassChange,
      leanMassChange,
      isPositiveBfChange: bfChange < 0, // снижение % жира считается положительным прогрессом
      isPositiveWeightChange: weightChange <= 0, // стабильный или снижающийся вес обычно считается положительным
      isPositiveFatMassChange: fatMassChange < 0, // снижение жировой массы считается положительным
      isPositiveLeanMassChange: leanMassChange > 0 // увеличение безжировой массы считается положительным
    };
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Сравнение измерений
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedMeasurements.length} {getSuffix(savedMeasurements.length)}
        </span>
      </div>

      {/* Прогресс с предыдущим измерением */}
      {showProgress && progressData && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
            <TrendingDown className="w-4 h-4 mr-1" />
            Прогресс с прошлого измерения
          </h4>
          
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">% жира:</span>
              <span className={progressData.isPositiveBfChange 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-red-500 dark:text-red-400'
              }>
                {progressData.bfChange > 0 ? '+' : ''}{formatNumber(progressData.bfChange, 1)}%
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Вес:</span>
              <span className={progressData.isPositiveWeightChange 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-gray-600 dark:text-gray-400'
              }>
                {progressData.weightChange > 0 ? '+' : ''}{formatNumber(progressData.weightChange, 1)} кг
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Жировая масса:</span>
              <span className={progressData.isPositiveFatMassChange 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-red-500 dark:text-red-400'
              }>
                {progressData.fatMassChange > 0 ? '+' : ''}{formatNumber(progressData.fatMassChange, 1)} кг
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Безжировая масса:</span>
              <span className={progressData.isPositiveLeanMassChange 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-orange-500 dark:text-orange-400'
              }>
                {progressData.leanMassChange > 0 ? '+' : ''}{formatNumber(progressData.leanMassChange, 1)} кг
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {sortedMeasurements.map((measurement) => (
          <div 
            key={measurement.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {measurement.name}
                </h4>
                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  {new Date(measurement.date).toLocaleDateString('ru-RU', { 
                    day: 'numeric', 
                    month: 'short',
                    year: 'numeric' 
                  })}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedMeasurement(measurement.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedMeasurement(measurement.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Процент жира:</span>
                <span className="font-medium">
                  {formatNumber(measurement.results.bodyFatPercentage, 1)}%
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Вес:</span>
                <span>
                  {formatNumber(measurement.results.weight, 1)} кг
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Жировая масса:</span>
                <span>
                  {formatNumber(measurement.results.bodyComposition.fatMass, 1)} кг
                </span>
              </div>
              <div className="flex justify-between">
                <span>Безжировая масса:</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {formatNumber(measurement.results.bodyComposition.leanMass, 1)} кг
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const getSuffix = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'измерений';
  }
  
  if (lastDigit === 1) {
    return 'измерение';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'измерения';
  }
  
  return 'измерений';
};

export default ComparisonSidebar;